import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { ManageShippingsViewModel } from "../../viewmodels/shipping/ManageShippingsViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";

import {
  classNames,
  formatDateWithTime,
  orderStatusOptions,
  withRouter,
  isSunDistribution,
} from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import { Link } from "react-router-dom";

import { Paginator } from "primereact/paginator";
import FilterDropdown from "../../components/filterDropdown/FilterDropdown";
import Pills from "../../components/controls/Pills";

const SUN_TABS = [
  { id: "shippings", name: "Shippings" },
  { id: "sun_orders", name: "Sun Orders" },
];

function ManageShippings(props) {
  const { addSnackbar, logout } = props;
  const {
    getShippings,
    getShippingSunOrders,
    loading,
    sunOrdersLoading,
    shippings,
    sunOrders,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    setShowFilters,
    showFilters,
    resetFilters,
    filterComponents,
    getCustomersForDropdown,
    filters,
    askDeleteShipping,
    openImportSpsForSun,
    createMultipleSunInvoices,
    selectedProducts,
    setSelectedProducts,
  } = ManageShippingsViewModel(logout, addSnackbar);

  const navigate = useNavigate();

  const [isSunFlow, setIsSunFlow] = useState(false);
  const [activeTab, setActiveTab] = useState("shippings");
  const [shippingsFirst, setShippingsFirst] = useState(0);
  const [shippingsRows, setShippingsRows] = useState(20);
  const [sunFirst, setSunFirst] = useState(0);
  const [sunRows, setSunRows] = useState(20);
  const [sunOrdersFetched, setSunOrdersFetched] = useState(false);

  const searchRef = useRef();

  useEffect(() => {
    const sunFlow = isSunDistribution();
    setIsSunFlow(sunFlow);
    getCustomersForDropdown();
    getShippings(null, 1, filters);
  }, []);

  useEffect(() => {
    if (!isSunFlow || activeTab !== "sun_orders" || sunOrdersFetched) return;
    getShippingSunOrders(searchRef.current?.value ?? null, filters);
    setSunOrdersFetched(true);
    // Fetch once when first entering Sun Orders tab; search refreshes explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSunFlow, activeTab, sunOrdersFetched]);

  const handleSearch = () => {
    const search = searchRef.current?.value ?? null;
    if (isSunFlow && activeTab === "sun_orders") {
      setSunFirst(0);
      getShippingSunOrders(search, filters);
      setSunOrdersFetched(true);
      return;
    }

    setShippingsFirst(0);
    getShippings(search, 1, filters);
  };

  const handleShippingsPagination = (event) => {
    setShippingsFirst(event.first);
    setShippingsRows(event.rows);
    getShippings(searchRef.current?.value ?? null, event.page + 1, filters);
  };

  const handleSunOrdersPagination = (event) => {
    setSunFirst(event.first);
    setSunRows(event.rows);
  };

  const convertStatus = useCallback((status) => {
    const statusOption = orderStatusOptions.filter((el) => el.value === status);

    return statusOption.length === 1
      ? { text: statusOption[0].label, type: statusOption[0].type }
      : { text: orderStatusOptions[0].label, type: orderStatusOptions[0].type };
  }, []);

  const dateCreatedBody = useCallback((rowData) => {
    return formatDateWithTime(rowData.created_at);
  }, []);

  const statusBody = useCallback(
    (rowData) => {
      return <Pills content={convertStatus(rowData.status)} />;
    },
    [convertStatus]
  );

  const invoiceBody = useCallback((rowData) => {
    const invoiceId = rowData.quickbooks_invoice_id;
    const display =
      rowData.invoice_number || invoiceId || null;

    if (!display || !invoiceId) {
      return "—";
    }

    return (
      <a
        href={`https://qbo.intuit.com/app/invoice?txnId=${invoiceId}`}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
      >
        {display}
      </a>
    );
  }, []);

  const shippingReportBody = useCallback((rowData) => {
    const hasReport = Number(rowData.has_shipping_report) === 1;

    return (
      <span
        className={classNames(
          hasReport
            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
            : "bg-slate-50 text-slate-600 ring-slate-500/20",
          "inline-flex rounded-full px-2 py-1 text-xs font-semibold ring-1 ring-inset"
        )}
      >
        {hasReport ? "Yes" : "No"}
      </span>
    );
  }, []);

  const massItemActionTemplate = useCallback(
    (rowData) => {
      if (rowData.record_type === "sun_order") {
        return (
          <div className="flex gap-2">
            <Link
              className="inline-flex items-center justify-center min-w-[70px] rounded-lg bg-amber-600 px-3.5 py-2 text-sm font-semibold text-white shadow-md hover:bg-amber-700 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 transition-all duration-200"
              to={`/sun-orders/${rowData.id}`}
            >
              Edit
            </Link>
          </div>
        );
      }

      return (
        <div>
          {isSunFlow ? (
            rowData.customer_id === 528 ? (
              <div className="flex gap-2">
                <Link
                  className="inline-flex items-center justify-center min-w-[70px] rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-md hover:bg-indigo-700 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 transition-all duration-200"
                  to={`/shippings-v2/${rowData.id}`}
                >
                  Edit
                </Link>
              </div>
            ) : null
          ) : (
            <div className="flex">
              <Link
                className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                to={`/shippings-v2/${rowData.id}`}
              >
                Edit
              </Link>
              <button
                onClick={() => {
                  askDeleteShipping(rowData);
                }}
                className="ml-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      );
    },
    [isSunFlow, askDeleteShipping]
  );

  const handleSelectionChange = useCallback(
    (e) => {
      setSelectedProducts(e.value);
    },
    [setSelectedProducts]
  );

  const shippingTableData = useMemo(() => {
    return (shippings.data ?? []).map((item) => ({
      ...item,
      record_type: "shipping",
      row_key: `shipping-${item.id}`,
      customer_display: isSunFlow ? item.sun_customer_name : item.customer,
      quickbooks_invoice_id:
        (isSunFlow
          ? item.sun_quickbooks_invoice_id
          : item.solmaz_quickbooks_invoice_id) || null,
    }));
  }, [isSunFlow, shippings.data]);

  const sunOrdersTableData = useMemo(() => {
    return (sunOrders ?? []).map((item) => ({
      ...item,
      record_type: "sun_order",
      row_key: `sun_order-${item.id}`,
      customer_display: item.customer,
      quickbooks_invoice_id: item.sun_quickbooks_invoice_id || null,
    }));
  }, [sunOrders]);

  const pagedSunOrders = useMemo(() => {
    return sunOrdersTableData.slice(sunFirst, sunFirst + sunRows);
  }, [sunOrdersTableData, sunFirst, sunRows]);

  const isSunOrdersTab = isSunFlow && activeTab === "sun_orders";
  const tableData = isSunOrdersTab ? pagedSunOrders : shippingTableData;
  const tableLoading = isSunOrdersTab ? sunOrdersLoading : loading;

  return (
    <div>
      <div className="mt-2 lg:px-8">
        <div className="flex justify-end mb-2">
          {!isSunFlow ? (
            <FilterDropdown
              actionCall={() => handleSearch()}
              filterComponents={filterComponents}
              resetFilters={() => {
                resetFilters();
              }}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
            />
          ) : null}
        </div>

        <div className="sm:flex sm:items-center px-4 py-5 card-custom">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Shippings {isSunFlow ? " (Only shipped orders)" : ""}
            </h1>
            <div className="mt-5 block sm:flex rounded-md space-y-4 sm:space-y-0">
              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <input
                  type="search"
                  name="searchProduct"
                  id="searchProduct"
                  className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                  placeholder="Search by name"
                  ref={searchRef}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />

                <button
                  type="button"
                  className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                  onClick={() => handleSearch()}
                >
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>
              </div>

              <div className="flex">
                {isSunFlow ? (
                  <>
                    <div>
                      <button
                        className="ml-2 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                        onClick={openImportSpsForSun}
                      >
                        Import SPS
                      </button>
                    </div>
                    <div>
                      <button
                        className="ml-2 rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                        onClick={createMultipleSunInvoices}
                      >
                        Create Invoice
                      </button>
                    </div>
                  </>
                ) : null}
                {!isSunFlow ? (
                  <div>
                    <button
                      className="ml-2 rounded-md bg-main-purple px-2 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={() => navigate("/create-shipping")}
                    >
                      Create Shipping
                    </button>
                  </div>
                ) : null}
                {isSunFlow ? (
                  <div>
                    <button
                      className="ml-2 rounded-md bg-amber-600 px-2 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
                      onClick={() => navigate("/sun-orders/new")}
                    >
                      Create Sun Shipping
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {isSunFlow ? (
          <div className="mt-4">
            <div className="sm:hidden">
              <label htmlFor="sun-shipping-tabs" className="sr-only">
                Select a tab
              </label>
              <select
                id="sun-shipping-tabs"
                name="sun-shipping-tabs"
                className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
              >
                {SUN_TABS.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden sm:block">
              <nav
                className="isolate flex divide-x divide-gray-200 rounded-lg shadow"
                aria-label="Tabs"
              >
                {SUN_TABS.map((tab, index) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={classNames(
                      activeTab === tab.id
                        ? "text-gray-900"
                        : "text-gray-500 hover:text-gray-700",
                      index === 0 ? "rounded-l-lg" : "",
                      index === SUN_TABS.length - 1 ? "rounded-r-lg" : "",
                      "group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
                    )}
                    aria-current={activeTab === tab.id ? "page" : undefined}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <span>{tab.name}</span>
                    <span
                      aria-hidden="true"
                      className={classNames(
                        activeTab === tab.id
                          ? "bg-indigo-500"
                          : "bg-transparent",
                        "absolute inset-x-0 bottom-0 h-0.5"
                      )}
                    />
                  </button>
                ))}
              </nav>
            </div>
          </div>
        ) : null}

        <div className="mt-8 flow-root overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            {tableLoading ? (
              <div>
                <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
                  <div className="animate-pulse flex space-x-4">Loading</div>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden card-custom">
                <DataTable
                  value={tableData}
                  selectionMode={"checkbox"}
                  selection={selectedProducts}
                  onSelectionChange={handleSelectionChange}
                  dataKey="row_key"
                  isDataSelectable={(e) =>
                    !isSunFlow || e.data.record_type === "shipping"
                  }
                  tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                  cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
                  resizableColumns={false}
                  sortable={false}
                >
                  <Column
                    selectionMode="multiple"
                    headerStyle={{ width: "5%" }}
                  ></Column>
                  <Column
                    field="po_number"
                    header="PO #"
                    headerStyle={{ width: "15%" }}
                  />
                  <Column
                    field="customer_display"
                    header="Customer"
                    headerStyle={{ width: "25%" }}
                  />
                  <Column
                    header="Invoice #"
                    body={invoiceBody}
                    headerStyle={{ width: "15%" }}
                  />
                  <Column
                    header="Date Created"
                    body={dateCreatedBody}
                    headerStyle={{ width: "15%" }}
                  />
                  <Column
                    header="Status"
                    body={statusBody}
                    headerStyle={{ width: "20%" }}
                  />
                  {isSunFlow ? (
                    <Column
                      header="Shipping Report"
                      body={shippingReportBody}
                      headerStyle={{ width: "15%" }}
                    />
                  ) : null}
                  <Column
                    header=""
                    body={massItemActionTemplate}
                    headerStyle={{ width: "20%" }}
                  />
                </DataTable>
              </div>
            )}
          </div>
          <div className="w-full">
            {isSunOrdersTab ? (
              <Paginator
                first={sunFirst}
                rows={sunRows}
                totalRecords={sunOrdersTableData.length}
                onPageChange={handleSunOrdersPagination}
              />
            ) : (
              <Paginator
                first={shippingsFirst}
                rows={shippingsRows}
                totalRecords={shippings.total}
                onPageChange={handleShippingsPagination}
              />
            )}
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        customClass="max-w-3xl"
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ManageShippings)
);
